/**
 * Error Reporter Service
 * Phase 5.5: Error Handling - Client-side error reporting
 *
 * Reports errors from the React app to the backend for logging
 */

export interface ErrorReport {
  /** Error name/type */
  name: string;
  /** Error message */
  message: string;
  /** Stack trace */
  stack?: string;
  /** Component stack from React */
  componentStack?: string;
  /** URL where error occurred */
  url: string;
  /** User agent */
  userAgent: string;
  /** Timestamp */
  timestamp: string;
  /** Additional context */
  context?: Record<string, unknown>;
}

export interface ErrorReporterConfig {
  /** API endpoint for error reporting */
  endpoint?: string;
  /** Maximum errors to send per minute (rate limiting) */
  maxErrorsPerMinute?: number;
  /** Whether to also log to console */
  logToConsole?: boolean;
  /** Whether reporting is enabled */
  enabled?: boolean;
}

const DEFAULT_CONFIG: Required<ErrorReporterConfig> = {
  endpoint: '/api/errors/report',
  maxErrorsPerMinute: 10,
  logToConsole: true,
  enabled: import.meta.env.PROD,
};

class ErrorReporter {
  private config: Required<ErrorReporterConfig>;
  private errorQueue: ErrorReport[] = [];
  private errorCount = 0;
  private lastResetTime = Date.now();
  private flushTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(config: ErrorReporterConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Configure the error reporter
   */
  configure(config: Partial<ErrorReporterConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Report an error
   */
  report(error: Error, context?: Record<string, unknown>): void {
    // Rate limiting check
    const now = Date.now();
    if (now - this.lastResetTime > 60000) {
      this.errorCount = 0;
      this.lastResetTime = now;
    }

    if (this.errorCount >= this.config.maxErrorsPerMinute) {
      return; // Rate limited
    }

    this.errorCount++;

    const report: ErrorReport = {
      name: error.name,
      message: error.message,
      stack: error.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      context,
    };

    if (this.config.logToConsole) {
      console.error('[ErrorReporter]', report);
    }

    if (this.config.enabled) {
      this.errorQueue.push(report);
      this.scheduleFlush();
    }
  }

  /**
   * Report a React component error
   */
  reportComponentError(
    error: Error,
    componentStack?: string,
    context?: Record<string, unknown>
  ): void {
    const now = Date.now();
    if (now - this.lastResetTime > 60000) {
      this.errorCount = 0;
      this.lastResetTime = now;
    }

    if (this.errorCount >= this.config.maxErrorsPerMinute) {
      return;
    }

    this.errorCount++;

    const report: ErrorReport = {
      name: error.name,
      message: error.message,
      stack: error.stack,
      componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      context,
    };

    if (this.config.logToConsole) {
      console.error('[ErrorReporter] Component error:', report);
    }

    if (this.config.enabled) {
      this.errorQueue.push(report);
      this.scheduleFlush();
    }
  }

  /**
   * Schedule a flush of queued errors
   */
  private scheduleFlush(): void {
    if (this.flushTimeout) return;

    // Debounce: batch errors for 1 second
    this.flushTimeout = setTimeout(() => {
      this.flush();
      this.flushTimeout = null;
    }, 1000);
  }

  /**
   * Flush queued errors to the server
   */
  async flush(): Promise<void> {
    if (this.errorQueue.length === 0) return;

    const errors = [...this.errorQueue];
    this.errorQueue = [];

    try {
      const response = await fetch(this.config.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ errors }),
        // Don't retry failed error reports
        keepalive: true,
      });

      if (!response.ok) {
        // Silently fail - don't report errors about reporting errors
        console.warn('[ErrorReporter] Failed to send error report:', response.status);
      }
    } catch {
      // Silently fail
      console.warn('[ErrorReporter] Failed to send error report');
    }
  }

  /**
   * Create a global error handler
   */
  setupGlobalHandler(): void {
    // Handle uncaught errors
    window.addEventListener('error', (event) => {
      if (event.error instanceof Error) {
        this.report(event.error, { type: 'uncaught' });
      } else {
        this.report(new Error(event.message), { type: 'uncaught', event: event.type });
      }
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      this.report(error, { type: 'unhandled_rejection' });
    });
  }
}

// Singleton instance
export const errorReporter = new ErrorReporter();

// Setup global handlers when module loads
if (typeof window !== 'undefined') {
  errorReporter.setupGlobalHandler();
}
